/**
 * NOS-140: Published campaign detail at /campaign/:slug with blocks + progress + P2P signup.
 */
import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { supabase } from "@/lib/supabase"
import { PublicSitePage } from "@/components/PublicSitePage"
import { SEOHead } from "@/components/public/SEOHead"
import { getPublicSiteOrigin } from "@/lib/publicSiteUrl"
import { extractDescriptionFromPageSections } from "@/lib/seo/extractMetaDescription"
import { donateActionJsonLd } from "@/lib/seo/structuredData"
import { usePublicSiteShell } from "@/hooks/usePublicSiteShell"
import { BlockRenderer } from "@/components/website-builder/BlockRenderer"
import { CampaignProgressBar } from "@/components/campaigns/CampaignProgressBar"
import type { Organization, OrgCustomizations, PageSection } from "@/templates/types"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { blockJsonToPageSections } from "@/lib/blockContentToPageSections"
import { campaignEnded, daysRemaining } from "@/lib/campaigns/campaignDates"
import type { Tables } from "@/types/database"
import { toast } from "sonner"
import { Copy, Check } from "lucide-react"

type CampaignRow = Tables<"campaigns">

export interface PublicCampaignDetailPageProps {
  orgSlug: string
  campaignSlug: string
  org?: Organization | null
  customizations?: OrgCustomizations | null
  basePath?: string
}

async function fetchCampaignTotals(campaignId: string): Promise<{ raised_cents: number; donor_count: number }> {
  const { data, error } = await supabase.rpc("get_campaign_public_totals", { cid: campaignId })
  if (error || !data?.length) return { raised_cents: 0, donor_count: 0 }
  const row = data[0] as { raised_cents: number; donor_count: number }
  return { raised_cents: Number(row.raised_cents), donor_count: Number(row.donor_count) }
}

export function PublicCampaignDetailPage({
  orgSlug,
  campaignSlug,
  org: orgProp,
  customizations: custProp,
  basePath = "",
}: PublicCampaignDetailPageProps) {
  const shell = usePublicSiteShell(orgSlug, {
    org: orgProp,
    customizations: custProp,
    basePath,
  })
  const { org: resolvedOrg, cust, orgName, primaryColor } = shell

  const [campaign, setCampaign] = useState<CampaignRow | null>(null)
  const [raisedCents, setRaisedCents] = useState(0)
  const [donorCount, setDonorCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const [fundOpen, setFundOpen] = useState(false)
  const [peerName, setPeerName] = useState("")
  const [peerEmail, setPeerEmail] = useState("")
  const [peerGoal, setPeerGoal] = useState("")
  const [peerSubmitting, setPeerSubmitting] = useState(false)
  const [shareToken, setShareToken] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const orgId = resolvedOrg?.id
  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!campaignSlug) {
        setNotFound(true)
        setLoading(false)
        return
      }
      if (!orgId) return

      const { data: camp, error: campErr } = await supabase
        .from("campaigns")
        .select("*")
        .eq("org_id", orgId)
        .eq("slug", campaignSlug)
        .eq("is_published", true)
        .maybeSingle()

      if (cancelled) return
      if (campErr || !camp) {
        setNotFound(true)
        setLoading(false)
        return
      }

      setCampaign(camp as CampaignRow)
      const totals = await fetchCampaignTotals((camp as CampaignRow).id)
      if (!cancelled) {
        setRaisedCents(totals.raised_cents)
        setDonorCount(totals.donor_count)
        setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [orgId, campaignSlug])

  const sectionBlocks: PageSection[] = useMemo(() => {
    if (!campaign) return []
    return blockJsonToPageSections(campaign.content_sections, campaign.org_id)
  }, [campaign])

  const ended = campaign ? campaignEnded(campaign.end_date) : false
  const daysLeft = campaign ? daysRemaining(campaign.end_date) : null
  const pctOfGoal =
    campaign && campaign.goal_cents > 0
      ? Math.min(100, Math.round((raisedCents / campaign.goal_cents) * 100))
      : 0

  const metaDescription = useMemo(() => {
    if (!campaign) return ""
    const fromBlocks = extractDescriptionFromPageSections(sectionBlocks)
    if (fromBlocks) return fromBlocks.length <= 160 ? fromBlocks : `${fromBlocks.slice(0, 159)}…`
    const g = campaign.goal_cents > 0 ? `Goal: $${(campaign.goal_cents / 100).toLocaleString()}. ` : ""
    const t = campaign.title
    const combined = `${g}${t}`
    return combined.length <= 160 ? combined : `${combined.slice(0, 159)}…`
  }, [campaign, sectionBlocks])

  const canonicalUrl = useMemo(() => {
    if (!resolvedOrg || !campaign) return ""
    const origin = getPublicSiteOrigin(resolvedOrg)
    return `${origin}/campaign/${campaign.slug}`
  }, [resolvedOrg, campaign])

  // NOS-242: DonateAction JSON-LD — campaign pages had no structured data.
  const campaignJsonLd = useMemo(() => {
    if (!campaign || !resolvedOrg || !canonicalUrl || ended) return null
    return donateActionJsonLd({
      campaignTitle: campaign.title,
      campaignUrl: canonicalUrl,
      description: metaDescription,
      orgName: resolvedOrg.name ?? "Organization",
      orgUrl: getPublicSiteOrigin(resolvedOrg),
    })
  }, [campaign, resolvedOrg, canonicalUrl, metaDescription, ended])

  const donateSearch = campaign && !ended ? `?campaign_id=${campaign.id}` : ""
  const donateHref = `${basePath}/donate${donateSearch}`

  const shareBase = resolvedOrg && campaign ? `${getPublicSiteOrigin(resolvedOrg)}/campaign/${campaign.slug}/fundraise` : ""

  async function submitPeerFundraiser(e: React.FormEvent) {
    e.preventDefault()
    if (!campaign) return
    const name = peerName.trim()
    const email = peerEmail.trim().toLowerCase()
    if (!name || !email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid name and email")
      return
    }
    let goalCents: number | null = null
    const raw = peerGoal.trim()
    if (raw) {
      const n = Math.round(parseFloat(raw) * 100)
      if (!Number.isFinite(n) || n <= 0) {
        toast.error("Personal goal must be a positive dollar amount")
        return
      }
      goalCents = n
    }

    setPeerSubmitting(true)
    try {
      const { data, error } = await supabase.rpc("create_peer_fundraiser", {
        p_campaign_id: campaign.id,
        p_name: name,
        p_email: email,
        p_personal_goal_cents: goalCents ?? undefined,
      })
      if (error) throw error
      const row = Array.isArray(data) ? data[0] : null
      const token =
        row && typeof row === "object" && "share_token" in row
          ? String((row as { share_token: string }).share_token)
          : null
      if (!token) throw new Error("No share token returned")
      setShareToken(token)
      toast.success("Your fundraising page is ready")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not create fundraiser")
    } finally {
      setPeerSubmitting(false)
    }
  }

  function copyShareUrl() {
    if (!shareBase || !shareToken) return
    const url = `${shareBase}/${shareToken}`
    void navigator.clipboard.writeText(url)
    setCopied(true)
    toast.success("Link copied")
    setTimeout(() => setCopied(false), 2000)
  }

  if (loading || shell.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (notFound || shell.notFound || !campaign || !resolvedOrg) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 px-4">
        <h1 className="text-2xl font-bold">404</h1>
        <p className="text-muted-foreground text-center">This campaign doesn’t exist or isn’t published.</p>
        <Button asChild variant="outline">
          <Link to={`${basePath}/campaign`}>View campaigns</Link>
        </Button>
      </div>
    )
  }

  return (
    <PublicSitePage
      shell={shell}
      className="bg-background"
      head={
        canonicalUrl ? (
          <SEOHead
            title={`${campaign.title} | ${orgName}`}
            description={metaDescription}
            canonicalUrl={canonicalUrl}
            ogType="website"
            imageUrl={campaign.cover_image_url}
            jsonLd={campaignJsonLd}
          />
        ) : null
      }
    >
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        {ended ? (
          <div
            className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            role="status"
          >
            This campaign has ended — thank you to everyone who supported {orgName}.
          </div>
        ) : null}

        {campaign.cover_image_url ? (
          <div className="mb-8 aspect-video overflow-hidden rounded-lg">
            <img src={campaign.cover_image_url} alt="" className="h-full w-full object-cover" />
          </div>
        ) : null}

        <header className="mb-6 space-y-4">
          <h1 className="text-3xl font-bold">{campaign.title}</h1>
          {!ended && daysLeft != null && daysLeft >= 0 ? (
            <p className="text-sm text-muted-foreground">{daysLeft} days remaining</p>
          ) : null}
          <CampaignProgressBar raisedCents={raisedCents} goalCents={campaign.goal_cents} ended={ended} />
          {ended ? (
            <div className="rounded-md border bg-muted/40 p-4 text-sm">
              <p className="font-medium">Final results</p>
              <ul className="mt-2 list-inside list-disc text-muted-foreground">
                <li>
                  ${(raisedCents / 100).toLocaleString()} raised ({donorCount} donors)
                </li>
                <li>{pctOfGoal}% of goal</li>
              </ul>
            </div>
          ) : null}
        </header>

        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <Button size="lg" asChild style={{ backgroundColor: primaryColor, borderColor: primaryColor }}>
            <Link to={donateHref}>{ended ? `Support ${orgName}` : "Donate to this campaign"}</Link>
          </Button>
          {!ended ? (
            <Button size="lg" variant="outline" type="button" onClick={() => setFundOpen((o) => !o)}>
              Fundraise for us
            </Button>
          ) : null}
        </div>

        {!ended && fundOpen ? (
          <section className="mb-10 rounded-lg border bg-card p-4 shadow-sm">
            <h2 className="text-lg font-semibold">Start your own fundraiser</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              We’ll create a personal page you can share. Your name and email are required.
            </p>
            {shareToken ? (
              <div className="mt-4 space-y-2">
                <Label>Your share link</Label>
                <div className="flex gap-2">
                  <Input readOnly className="font-mono text-xs" value={`${shareBase}/${shareToken}`} />
                  <Button type="button" size="icon" variant="outline" onClick={() => copyShareUrl()}>
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            ) : (
              <form className="mt-4 space-y-4" onSubmit={(e) => void submitPeerFundraiser(e)}>
                <div className="space-y-2">
                  <Label htmlFor="peer-name">Name</Label>
                  <Input
                    id="peer-name"
                    value={peerName}
                    onChange={(e) => setPeerName(e.target.value)}
                    autoComplete="name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="peer-email">Email</Label>
                  <Input
                    id="peer-email"
                    type="email"
                    value={peerEmail}
                    onChange={(e) => setPeerEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="peer-goal">Personal goal (USD, optional)</Label>
                  <Input
                    id="peer-goal"
                    inputMode="decimal"
                    placeholder="e.g. 500"
                    value={peerGoal}
                    onChange={(e) => setPeerGoal(e.target.value)}
                  />
                </div>
                <Button type="submit" disabled={peerSubmitting}>
                  {peerSubmitting ? "Creating…" : "Create my page"}
                </Button>
              </form>
            )}
          </section>
        ) : null}

        {/* NOS-221: no `prose` wrapper around section blocks (see BlogPostPage) */}
        <div className="max-w-none">
          {sectionBlocks
            .sort((a, b) => a.order_index - b.order_index)
            .map((section) => (
              <BlockRenderer key={section.id} section={section} customizations={cust} org={resolvedOrg} />
            ))}
        </div>
      </main>
    </PublicSitePage>
  )
}
